import React, {
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from 'react'
import * as echarts from 'echarts'
import { Box } from '../../atoms/Box'
import { Text } from '../../atoms/Text'
import { Loading } from '../../atoms/Loading'
import { Button } from '../../atoms/Button'
import { ButtonGroup } from '../../molecules/ButtonGroup'
import { StatsContainer } from '../../molecules/StatsContainer'
import {
  ChartContainer,
  ChartSection,
  ChartTitle,
  ErrorContainer,
  NotAvailable,
  PrintWrapper,
} from '../../molecules/ChartComponents'
import {
  formatNumber,
  formatPercentage,
  getTreemapData,
  getChartColors,
  generateInsights,
  processEvolutionData,
  processPortalData,
  formatPricePerSqm,
} from './PortalAnalysisChart.utils'
import {
  PortalAnalysisChartProps,
  PortalAnalysisChartRef,
  ViewType,
} from './PortalAnalysisChart.types'

const VIEW_OPTIONS: { value: ViewType; label: string }[] = [
  { value: 'listings', label: 'Listing Count' },
  { value: 'value', label: 'Market Value (Price)' },
  { value: 'regions', label: 'Regional Coverage' },
]

export const PortalAnalysisChart = forwardRef<
  PortalAnalysisChartRef,
  PortalAnalysisChartProps
>(
  (
    {
      data = {
        portals: [],
        totalListings: 0,
        totalPortals: 0,
        marketLeader: null,
        top5Share: 0,
      },
      filters,
      evolutionData,
      loading = false,
      error = null,
      showEvolution = false,
      showInsights = true,
      showControls = true,
      accentColor = '#00ade6',
      t = (key: string) => key,
    },
    ref
  ) => {
    const [viewType, setViewType] = useState<ViewType>('listings')
    const treemapRef = useRef<HTMLDivElement>(null)
    const evolutionChartRef = useRef<HTMLDivElement>(null)
    const chartInstance = useRef<echarts.ECharts | null>(null)
    const evolutionChartInstance = useRef<echarts.ECharts | null>(null)
    const isPrintingRef = useRef(false)

    useEffect(() => {
      if (!loading && !error && data && processedData.portals.length > 0) {
        createChart()
      }

      return () => {
        chartInstance.current?.dispose()
      }
    }, [data, viewType, loading, error])

    // Create evolution chart
    useEffect(() => {
      if (!evolutionData) return

      const processedData = processEvolutionData(evolutionData)
      if (!processedData || !evolutionChartRef.current) return

      createEvolutionChart(processedData)

      return () => {
        evolutionChartInstance.current?.dispose()
      }
    }, [evolutionData])

    const processedData = processPortalData(data)

    // Calculate KPIs and insights early for the hook
    const insights =
      showInsights && processedData.portals.length > 0
        ? generateInsights(processedData, t)
        : []

    // Calculate KPIs
    const sortedPortals = [...processedData.portals].sort((a, b) => {
      switch (viewType) {
        case 'listings':
          return b.listingCount - a.listingCount
        case 'value':
          return b.totalValue - a.totalValue
        case 'regions':
          return b.regionCount - a.regionCount
        default:
          return b.listingCount - a.listingCount
      }
    })

    const marketLeader = sortedPortals[0]
    const top5 = sortedPortals.slice(0, 5)

    const getMarketShare = (portal: typeof marketLeader) => {
      if (!portal) return 0
      switch (viewType) {
        case 'listings':
          return processedData.totalListings > 0
            ? (portal.listingCount / processedData.totalListings) * 100
            : 0
        case 'value': {
          const totalValue = processedData.portals.reduce(
            (sum, p) => sum + p.totalValue,
            0
          )
          return totalValue > 0 ? (portal.totalValue / totalValue) * 100 : 0
        }
        case 'regions':
          return portal.marketShare
        default:
          return portal.marketShare
      }
    }

    const top5Share = top5.reduce(
      (sum, portal) => sum + getMarketShare(portal),
      0
    )

    const getMarketLeaderLabel = () => {
      switch (viewType) {
        case 'listings':
          return 'Most Listings'
        case 'value':
          return 'Highest Market Value'
        case 'regions':
          return 'Widest Coverage'
        default:
          return 'Market Leader'
      }
    }

    const totalValuePricePerSqm = processedData.portals.reduce(
      (sum, p) => sum + p.avgPrice * p.listingCount,
      0
    )
    const totalListings = processedData.portals.reduce(
      (sum, p) => sum + p.listingCount,
      0
    )
    const avgPrice =
      totalListings > 0 ? totalValuePricePerSqm / totalListings : 0

    const kpiData = [
      {
        id: 'total-portals',
        label: 'Total Portals',
        value: processedData.totalPortals.toString(),
        variant: 'default' as const,
      },
      {
        id: 'market-leader',
        label: getMarketLeaderLabel(),
        value: marketLeader ? marketLeader.name : '-',
        variant: 'default' as const,
      },
      {
        id: 'top5-share',
        label: viewType === 'regions' ? 'Top 5 Coverage' : 'Top 5 Market Share',
        value: formatPercentage(top5Share),
        variant: 'default' as const,
      },
      {
        id: 'avg-price-per-sqm',
        label: 'Average Price',
        value: formatPricePerSqm(avgPrice),
        variant: 'default' as const,
      },
    ]

    // Expose methods via ref (must be called before any conditional returns)
    useImperativeHandle(ref, () => ({
      exportChart: () => {
        if (chartInstance.current) {
          chartInstance.current.resize()
          return chartInstance.current.getDataURL({
            type: 'png',
            pixelRatio: 3,
            backgroundColor: '#fff',
            excludeComponents: ['toolbox', 'dataZoom'],
          })
        }
        return null
      },
      exportEvolutionChart: () => {
        if (evolutionChartInstance.current) {
          evolutionChartInstance.current.resize()
          return evolutionChartInstance.current.getDataURL({
            type: 'png',
            pixelRatio: 3,
            backgroundColor: '#fff',
            excludeComponents: ['toolbox', 'dataZoom'],
          })
        }
        return null
      },
      getKPIs: () => kpiData,
      getInsights: () => insights,
    }))

    const createChart = () => {
      if (!treemapRef.current || !data) return

      // Create chart instance
      if (chartInstance.current) {
        chartInstance.current.dispose()
      }

      chartInstance.current = echarts.init(treemapRef.current, undefined, {
        renderer: 'canvas',
        devicePixelRatio: isPrintingRef.current
          ? 1
          : window.devicePixelRatio || 1,
      })

      // Force resize if in print mode
      if (isPrintingRef.current) {
        setTimeout(() => {
          chartInstance.current?.resize()
        }, 50)
      }

      const treeData = getTreemapData(processedData.portals, viewType)
      const option = createTreemapOption(treeData, viewType)
      chartInstance.current.setOption(option)
    }

    const createEvolutionChart = (processedData: any) => {
      if (!evolutionChartRef.current) return

      // Dispose existing instance
      if (evolutionChartInstance.current) {
        evolutionChartInstance.current.dispose()
      }

      evolutionChartInstance.current = echarts.init(
        evolutionChartRef.current,
        undefined,
        {
          renderer: 'canvas',
          devicePixelRatio: isPrintingRef.current
            ? 1
            : window.devicePixelRatio || 1,
        }
      )

      // Format quarter for display
      const formatQuarter = (dateStr: string) => {
        const date = new Date(dateStr)
        const quarter = Math.floor(date.getMonth() / 3) + 1
        return `Q${quarter} ${date.getFullYear()}`
      }

      // Create series data
      const series = processedData.portals.map(
        (portal: string, index: number) => {
          const colors = getChartColors()
          return {
            name: portal,
            type: 'line',
            smooth: true,
            symbol: 'circle',
            symbolSize: 6,
            lineStyle: { width: 2 },
            itemStyle: {
              color: colors[index % colors.length],
            },
            data: processedData.seriesData[portal].map((d: any) => [
              d.quarter,
              d.marketShare,
            ]),
            emphasis: {
              focus: 'series',
              lineStyle: { width: 3 },
            },
          }
        }
      )

      const option = {
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderColor: '#e5e7eb',
          borderWidth: 1,
          borderRadius: 8,
          formatter: (params: any) => {
            const quarter = formatQuarter(params[0].axisValue)
            let html = `<div style="font-weight: 600; margin-bottom: 8px;">${quarter}</div>`

            // Sort by market share descending
            const sortedParams = [...params].sort(
              (a, b) => b.value[1] - a.value[1]
            )

            sortedParams.forEach((param: any) => {
              html += `
              <div style="display: flex; align-items: center; margin: 4px 0;">
                <span style="display: inline-block; width: 10px; height: 10px; background: ${param.color}; border-radius: 50%; margin-right: 8px;"></span>
                <span style="flex: 1;">${param.seriesName}:</span>
                <span style="font-weight: 600;">${formatPercentage(param.value[1])}</span>
              </div>
            `
            })
            return html
          },
        },
        legend: {
          type: 'scroll',
          orient: 'horizontal',
          bottom: 0,
          textStyle: { fontSize: 12 },
          pageIconSize: 12,
          pageTextStyle: { fontSize: 12 },
        },
        grid: {
          left: '3%',
          right: '3%',
          bottom: '20%',
          top: '5%',
          containLabel: true,
        },
        xAxis: {
          type: 'time',
          boundaryGap: false,
          axisLine: {
            lineStyle: { color: '#e5e7eb' },
          },
          axisLabel: {
            color: '#6c757d',
            formatter: (value: number) =>
              formatQuarter(new Date(value).toISOString()),
          },
        },
        yAxis: {
          type: 'value',
          name: 'Market Share (%)',
          nameLocation: 'middle',
          nameGap: 50,
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: {
            color: '#6c757d',
            formatter: '{value}%',
          },
          splitLine: {
            lineStyle: { color: '#f0f0f0' },
          },
        },
        series,
        dataZoom: [
          { type: 'inside', start: 0, end: 100 },
          {
            type: 'slider',
            start: 0,
            end: 100,
            height: 20,
            bottom: 80,
            borderColor: 'transparent',
            backgroundColor: '#f5f5f5',
            handleSize: '80%',
            handleStyle: { color: '#00ade6' },
            dataBackground: {
              lineStyle: { color: '#8392A5' },
              areaStyle: { color: '#8392A5' },
            },
          },
        ],
      }

      evolutionChartInstance.current.setOption(option)
    }

    const createTreemapOption = (treeData: any, currentViewType: ViewType) => {
      return {
        tooltip: {
          trigger: 'item',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderColor: '#e5e7eb',
          borderWidth: 1,
          borderRadius: 8,
          formatter: (params: any) => {
            if (!params.data.data) {
              return `<strong>${params.name}</strong><br/>
                   Total: ${formatNumber(params.value)}`
            }

            const portal = params.data.data
            const label =
              currentViewType === 'listings'
                ? 'Listings'
                : currentViewType === 'value'
                  ? 'Market Value'
                  : 'Regional Coverage'

            return `<strong>${params.name}</strong><br/>
                 ${label}: ${formatNumber(params.value)}<br/>
                 Listings: ${formatNumber(portal.listingCount)}<br/>
                 Market Share: ${formatPercentage(portal.marketShare)}<br/>
                 Avg Price: ${formatPricePerSqm(portal.avgPrice)}<br/>
                 Regions: ${portal.regionCount}`
          },
        },
        series: [
          {
            type: 'treemap',
            data: [treeData],
            leafDepth: 2,
            roam: 'move',
            label: {
              show: true,
              formatter: (params: any) => {
                // Adjust threshold based on view type
                let threshold = 50
                if (currentViewType === 'regions') {
                  threshold = 1 // Show labels for portals with 1+ regions
                } else if (currentViewType === 'value') {
                  threshold = 1000000 // Show labels for portals with 1M+ value
                }

                if (params.value < threshold) return ''
                return params.name
              },
              fontSize: 12,
            },
            upperLabel: {
              show: true,
              height: 30,
              fontSize: 14,
              fontWeight: 'bold',
            },
            levels: [
              {
                itemStyle: {
                  borderColor: '#fff',
                  borderWidth: 4,
                  gapWidth: 4,
                },
              },
              {
                itemStyle: {
                  borderColor: '#fff',
                  borderWidth: 2,
                  gapWidth: 2,
                },
              },
            ],
            breadcrumb: {
              show: true,
              top: '10',
              left: '10',
            },
          },
        ],
      }
    }

    // Handle window resize and print events
    useEffect(() => {
      const handleResize = () => {
        chartInstance.current?.resize()
        evolutionChartInstance.current?.resize()
      }

      const handleBeforePrint = () => {
        isPrintingRef.current = true
        const resizeChart = () => {
          if (chartInstance.current) {
            chartInstance.current.hideLoading()
            chartInstance.current.resize()
          }
          if (evolutionChartInstance.current) {
            evolutionChartInstance.current.resize()
          }
        }
        resizeChart()
      }

      const handleAfterPrint = () => {
        isPrintingRef.current = false
        setTimeout(() => {
          chartInstance.current?.resize()
          evolutionChartInstance.current?.resize()
        }, 100)
      }

      const mediaQueryList = window.matchMedia('print')
      const handleMediaQueryChange = (e: any) => {
        if (e.matches) {
          handleBeforePrint()
        } else {
          handleAfterPrint()
        }
      }

      if (mediaQueryList.matches) {
        handleBeforePrint()
      }

      window.addEventListener('resize', handleResize)
      window.addEventListener('beforeprint', handleBeforePrint)
      window.addEventListener('afterprint', handleAfterPrint)

      if (mediaQueryList.addEventListener) {
        mediaQueryList.addEventListener('change', handleMediaQueryChange)
      } else {
        mediaQueryList.addListener(handleMediaQueryChange)
      }

      return () => {
        window.removeEventListener('resize', handleResize)
        window.removeEventListener('beforeprint', handleBeforePrint)
        window.removeEventListener('afterprint', handleAfterPrint)

        if (mediaQueryList.removeEventListener) {
          mediaQueryList.removeEventListener('change', handleMediaQueryChange)
        } else {
          mediaQueryList.removeListener(handleMediaQueryChange)
        }
      }
    }, [])

    if (loading) {
      return (
        <Box style={{ textAlign: 'center', padding: '40px' }}>
          <Loading variant="spinner" size="large" center />
          <Text variant="body1" color="secondary" style={{ marginTop: '16px' }}>
            Loading portal analysis data...
          </Text>
        </Box>
      )
    }

    if (error) {
      return (
        <ErrorContainer variant="error">
          <Text variant="body1">{error}</Text>
        </ErrorContainer>
      )
    }

    if (!data || processedData.portals.length === 0) {
      return (
        <NotAvailable
          title="No Data Available"
          description="No portal data is available for the selected filters."
        />
      )
    }

    return (
      <PrintWrapper>
        <Box marginBottom="30px">
          <StatsContainer fullWidth stats={kpiData} />
        </Box>

        <ChartSection>
          <Box marginBottom="24px">
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              flexWrap="wrap"
              gap="16px"
            >
              <ChartTitle>
                Swiss Real Estate{' '}
                {filters.deal_type === 'rent' ? 'Rental' : 'Purchase'} Portals
                Market Share
              </ChartTitle>
              {showControls && (
                <Box
                  display="flex"
                  gap="16px"
                  flexWrap="wrap"
                  alignItems="center"
                >
                  <ButtonGroup>
                    {VIEW_OPTIONS.map((option) => (
                      <Button
                        key={option.value}
                        variant={
                          viewType === option.value ? 'primary' : 'secondary'
                        }
                        size="small"
                        onClick={() => setViewType(option.value)}
                      >
                        {option.label}
                      </Button>
                    ))}
                  </ButtonGroup>
                </Box>
              )}
            </Box>
          </Box>
          <ChartContainer height={600} ref={treemapRef} />
        </ChartSection>

        {/* Market Share Evolution Chart */}
        {showEvolution && (
          <ChartSection style={{ marginTop: '12px' }}>
            <Box marginBottom="24px">
              <ChartTitle>
                Key {filters.deal_type === 'rent' ? 'Rental' : 'Purchase'}{' '}
                Portals Market Share Evolution
              </ChartTitle>
              <Text
                variant="body2"
                color="secondary"
                style={{ marginTop: '8px' }}
              >
                Market share percentage by quarter for major Swiss real estate
                portals
              </Text>
            </Box>
            {processEvolutionData.length < 1 ? (
              <Box style={{ textAlign: 'center', padding: '40px' }}>
                <Loading variant="spinner" size="medium" center />
                <Text
                  variant="body2"
                  color="secondary"
                  style={{ marginTop: '16px' }}
                >
                  Loading market share evolution data...
                </Text>
              </Box>
            ) : (
              <ChartContainer height={500} ref={evolutionChartRef} />
            )}
          </ChartSection>
        )}

        {insights.length > 0 && (
          <Box marginTop="30px">
            <ChartTitle style={{ marginBottom: '24px' }}>
              Key Insights
            </ChartTitle>
            <Box display="flex" flexWrap="wrap" gap="24px">
              {insights.map((insight, index) => (
                <Box
                  key={index}
                  flex="1 1 280px"
                  style={{
                    background: '#f8f9fa',
                    borderRadius: '12px',
                    padding: '20px',
                    borderLeft: `4px solid ${accentColor}`,
                    minWidth: 0,
                  }}
                >
                  <Text
                    variant="h4"
                    style={{
                      fontSize: '16px',
                      fontWeight: 600,
                      marginBottom: '8px',
                      color: '#1a1a1a',
                    }}
                  >
                    {insight.title}
                  </Text>
                  <Text
                    variant="body2"
                    style={{
                      fontSize: '14px',
                      lineHeight: 1.5,
                      color: '#6c757d',
                    }}
                  >
                    {insight.description}
                  </Text>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </PrintWrapper>
    )
  }
)

PortalAnalysisChart.displayName = 'PortalAnalysisChart'
